import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  AccountBalance as AccountingIcon,
  Payment as PaymentIcon,
  CheckCircle as PaidIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as PendingIcon,
  Add as CreateBatchIcon,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  MonetizationOn as MoneyIcon,
  People as VendorsIcon,
  ShoppingCart as OrdersIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface UnpaidOrder {
  order_id: number;
  order_date: string;
  customer_name: string;
  order_total: number;
  vendor_id: string;
  vendor_name: string;
  vendor_email: string;
  vendor_commission_rate: number;
  platform_commission_rate: number;
  vendor_amount: number;
  platform_amount: number;
  shipping_total?: number;
  original_order_total?: number;
}

interface PaymentBatch {
  id: number;
  batch_name: string;
  payment_date: string;
  total_amount: number;
  total_orders: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  created_by_name: string;
  created_at: string;
  payment_count: number;
  paid_count: number;
}

interface VendorPaymentSummary {
  vendor_id: string;
  vendor_name: string;
  vendor_email: string;
  pending_orders: number;
  total_amount_due: number;
  total_platform_amount: number;
  total_order_value: number;
  oldest_order_date: string;
  newest_order_date: string;
  bank_details: any;
  payment_method: string;
  orders: Array<{
    order_id: number;
    order_date: string;
    order_total: number;
    vendor_amount: number;
    platform_amount: number;
    shipping_total: number;
    original_order_total: number;
  }>;
}

interface PaymentSummary {
  overall: {
    total_payments: number;
    total_vendor_amount: number;
    total_platform_amount: number;
    total_order_value: number;
    pending_payments: number;
    paid_payments: number;
    pending_amount: number;
    paid_amount: number;
  };
  top_vendors: Array<{
    vendor_id: string;
    vendor_name: string;
    total_orders: number;
    total_vendor_amount: number;
    pending_amount: number;
    paid_amount: number;
  }>;
}

interface BatchDetail {
  id: number;
  batch_name: string;
  payment_date: string;
  status: string;
  total_amount: number;
  created_by_name: string;
  created_at: string;
  notes: string;
  payments: Array<{
    id: number;
    order_id: number;
    vendor_id: string;
    vendor_name: string;
    customer_name: string;
    order_date: string;
    order_total: number;
    vendor_amount: number;
    platform_amount: number;
    payment_status: 'pending' | 'paid' | 'failed';
    payment_date: string;
    payment_method: string;
    payment_reference: string;
    notes: string;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`accounting-tabpanel-${index}`}
      aria-labelledby={`accounting-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Accounting: React.FC = () => {
  // State Management
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [createBatchDialogOpen, setCreateBatchDialogOpen] = useState(false);
  const [batchDetailDialogOpen, setBatchDetailDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<PaymentBatch | null>(null);
  const [batchDetails, setBatchDetails] = useState<BatchDetail | null>(null);
  const [batchFormData, setBatchFormData] = useState({
    batch_name: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);



  const queryClient = useQueryClient();

  // API Queries
  const { data: unpaidOrders, isLoading: unpaidLoading, error: unpaidError } = useQuery({
    queryKey: ['unpaid-orders'],
    queryFn: () => api.getUnpaidOrders(),
  });

  const { data: paymentBatches, isLoading: batchesLoading } = useQuery({
    queryKey: ['payment-batches'],
    queryFn: () => api.getPaymentBatches(),
  });

  const { data: paymentSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['payment-summary'],
    queryFn: () => api.getPaymentSummary(),
  });

  const { data: vendorPaymentSummary, isLoading: vendorSummaryLoading } = useQuery({
    queryKey: ['vendor-payment-summary'],
    queryFn: () => api.getVendorPaymentSummary(),
  });

  // Fetch comprehensive vendor data (same method as vendors page)
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors-for-accounting'],
    queryFn: async () => {
      const response = await fetch('https://api.erp.oroud.net/api/vendors.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Create vendor lookup map from comprehensive vendor data
  const vendorLookup = useMemo(() => {
    const lookup: Record<string, { name: string; email: string; phone?: string }> = {};
    
    if (vendorsData) {
      const rawVendors = Array.isArray(vendorsData?.data)
        ? vendorsData.data
        : Array.isArray(vendorsData?.vendors)
          ? vendorsData.vendors
          : Array.isArray(vendorsData)
            ? vendorsData
            : [];

      rawVendors.forEach((vendor: any) => {
        if (vendor.id) {
          lookup[vendor.id.toString()] = {
            name: vendor.store_name || vendor.shop_name || vendor.name || `Store #${vendor.id}`,
            email: vendor.email || '',
            phone: vendor.phone || ''
          };
        }
      });
    }
    
    return lookup;
  }, [vendorsData]);

  // Helper function to get vendor display name
  const getVendorDisplayName = (vendorId: string, fallbackName?: string) => {
    if (vendorLookup[vendorId]) {
      return vendorLookup[vendorId].name;
    }
    return fallbackName || `Store #${vendorId}`;
  };

  // Helper function to get vendor email
  const getVendorEmail = (vendorId: string, fallbackEmail?: string) => {
    if (vendorLookup[vendorId]) {
      return vendorLookup[vendorId].email;
    }
    return fallbackEmail || '';
  };

  // Mutations
  const createBatchMutation = useMutation({
    mutationFn: (data: any) => api.createPaymentBatch(data),
    onSuccess: () => {
      showNotification('Payment batch created successfully!', 'success');
      setCreateBatchDialogOpen(false);
      setSelectedOrders([]);
      setBatchFormData({ batch_name: '', payment_date: new Date().toISOString().split('T')[0], notes: '' });
      queryClient.invalidateQueries({ queryKey: ['unpaid-orders'] });
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-summary'] });
    },
    onError: (error: any) => {
      showNotification('Failed to create payment batch: ' + error.message, 'error');
    },
  });

  const deleteBatchMutation = useMutation({
    mutationFn: (data: { batch_id: number; performed_by: number }) => api.deletePaymentBatch(data),
    onSuccess: (data: any) => {
      showNotification(data.message || 'Payment batch deleted successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['unpaid-orders'] });
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-summary'] });
    },
    onError: (error: any) => {
      showNotification('Failed to delete payment batch: ' + error.message, 'error');
    },
  });

  const updatePaymentStatusMutation = useMutation({
    mutationFn: (data: { payment_id: number; status: string; notes?: string; performed_by: number }) => 
      api.updatePaymentStatus(data),
    onSuccess: () => {
      showNotification('Payment status updated successfully!', 'success');
      // Refresh batch details and other data
      if (selectedBatch) {
        fetchBatchDetails(selectedBatch.id);
      }
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-summary'] });
    },
    onError: (error: any) => {
      showNotification('Failed to update payment status: ' + error.message, 'error');
    },
  });

  const completeBatchMutation = useMutation({
    mutationFn: (data: { batch_id: number; performed_by: number }) => api.completeBatch(data),
    onSuccess: () => {
      showNotification('Payment batch marked as completed!', 'success');
      setBatchDetailDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-summary'] });
    },
    onError: (error: any) => {
      showNotification('Failed to complete batch: ' + error.message, 'error');
    },
  });

  // Helper Functions
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ open: true, message, severity });
  };

  // Fetch batch details
  const fetchBatchDetails = async (batchId: number) => {
    try {
      const response = await fetch(`https://api.erp.oroud.net/api/accounting.php?action=get_batch_details&batch_id=${batchId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      
      if (result.success) {
        setBatchDetails(result.data);
      } else {
        showNotification('Failed to load batch details: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error loading batch details', 'error');
    }
  };

  // Handle view batch details
  const handleViewBatch = async (batch: PaymentBatch) => {
    setSelectedBatch(batch);
    setBatchDetailDialogOpen(true);
    await fetchBatchDetails(batch.id);
  };

  // Handle mark payment as paid
  const handleMarkPaymentPaid = (paymentId: number) => {
    const notes = prompt('Enter payment reference and notes (optional):');
    
    updatePaymentStatusMutation.mutate({
      payment_id: paymentId,
      status: 'paid',
      notes: notes || undefined,
      performed_by: 1, // TODO: Get from auth context
    });
  };

  // Handle mark payment as failed
  const handleMarkPaymentFailed = (paymentId: number) => {
    const notes = prompt('Enter failure reason:');
    
    updatePaymentStatusMutation.mutate({
      payment_id: paymentId,
      status: 'failed',
      notes: notes || undefined,
      performed_by: 1, // TODO: Get from auth context
    });
  };

  // Handle complete entire batch
  const handleCompleteBatch = (batchId: number) => {
    if (window.confirm('Are you sure you want to mark this entire batch as completed? This will mark all pending payments as paid.')) {
      completeBatchMutation.mutate({
        batch_id: batchId,
        performed_by: 1, // TODO: Get from auth context
      });
    }
  };

  // Sync new completed orders function
  const syncNewOrders = async (showResult = true) => {
    setIsSyncing(true);
    try {
      const response = await fetch('https://api.erp.oroud.net/api/accounting.php?action=sync_new_orders', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      
      if (result.success) {
        const newOrdersCount = result.new_orders_added || 0;
        const skippedCount = result.skipped_existing || 0;
        const skippedParentCount = result.skipped_parent_orders || 0;
        
        if (showResult) {
          if (newOrdersCount > 0) {
            let message = `✅ Smart Sync completed! Added ${newOrdersCount} new vendor orders.`;
            if (skippedCount > 0) {
              message += ` ${skippedCount} were already in system.`;
            }
            if (skippedParentCount > 0) {
              message += ` 🧹 Automatically skipped ${skippedParentCount} parent orders.`;
            }
            showNotification(message, 'success');
          } else {
            let message = `✅ No new orders to sync.`;
            if (skippedCount > 0) {
              message += ` All ${skippedCount} completed orders are already in the system.`;
            }
            if (skippedParentCount > 0) {
              message += ` 🧹 Skipped ${skippedParentCount} parent orders.`;
            }
            showNotification(message, 'info');
          }
        }
        
        // Refresh all accounting data
        queryClient.invalidateQueries({ queryKey: ['unpaid-orders'] });
        queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
        queryClient.invalidateQueries({ queryKey: ['payment-summary'] });
        queryClient.invalidateQueries({ queryKey: ['vendor-payment-summary'] });
        
        setLastSyncTime(new Date().toLocaleTimeString());
      } else {
        if (showResult) {
          showNotification('Failed to sync orders: ' + result.error, 'error');
        }
      }
    } catch (error) {
      if (showResult) {
        showNotification('Error syncing new orders', 'error');
      }
    } finally {
      setIsSyncing(false);
    }
  };



  // Auto-sync new orders when page loads
  useEffect(() => {
    // Auto-sync after initial data loads (silent sync)
    const timer = setTimeout(() => {
      if (!unpaidLoading && !vendorSummaryLoading) {
        syncNewOrders(false); // Silent sync on load
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [unpaidLoading, vendorSummaryLoading]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSelectOrder = (orderId: number) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = () => {
    if (unpaidOrders?.data?.data) {
      const allOrderIds = unpaidOrders.data.data.map((order: UnpaidOrder) => order.order_id);
      setSelectedOrders(selectedOrders.length === allOrderIds.length ? [] : allOrderIds);
    }
  };

  const handleCreateBatch = () => {
    if (selectedOrders.length === 0) {
      showNotification('Please select orders to create a payment batch', 'warning');
      return;
    }
    
    const today = new Date();
    const batchName = `Payment Batch ${today.toISOString().split('T')[0]}`;
    setBatchFormData(prev => ({ ...prev, batch_name: batchName }));
    setCreateBatchDialogOpen(true);
  };

  const handleSubmitBatch = () => {
    createBatchMutation.mutate({
      order_ids: selectedOrders,
      batch_name: batchFormData.batch_name,
      payment_date: batchFormData.payment_date,
      notes: batchFormData.notes,
      created_by: 1, // TODO: Get from auth context
    });
  };

  const handleDeleteBatch = (batchId: number, batchName: string) => {
    if (window.confirm(`Are you sure you want to delete "${batchName}"? This will return all orders back to unpaid status.`)) {
      deleteBatchMutation.mutate({
        batch_id: batchId,
        performed_by: 1, // TODO: Get from auth context
      });
    }
  };

  const handleCreateVendorBatch = (vendor: VendorPaymentSummary) => {
    const orderIds = vendor.orders.map(order => order.order_id);
    const batchName = `${vendor.vendor_name} - ${new Date().toISOString().split('T')[0]}`;
    
    createBatchMutation.mutate({
      order_ids: orderIds,
      batch_name: batchName,
      payment_date: new Date().toISOString().split('T')[0],
      notes: `Payment batch for ${vendor.vendor_name} (${vendor.pending_orders} orders)`,
      created_by: 1, // TODO: Get from auth context
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (unpaidLoading || batchesLoading || summaryLoading || vendorSummaryLoading || vendorsLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Accounting</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            <AccountingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Accounting & Vendor Payments
          </Typography>
          
          {/* Vendor Resolution Status */}
          {vendorsData && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`${Object.keys(vendorLookup).length} vendors loaded`}
              color="success"
              size="small"
            />
          )}
          
          {/* Last Sync Time */}
          {lastSyncTime && (
            <Chip
              label={`Last synced: ${lastSyncTime}`}
              color="info"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Smart Sync New Orders Button */}
          <Tooltip title="Smart Sync automatically detects and skips parent orders, keeping only vendor-specific sub-orders">
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={isSyncing ? <CircularProgress size={16} /> : undefined}
              onClick={() => syncNewOrders(true)}
              disabled={isSyncing}
              sx={{ minWidth: 'auto' }}
            >
              {isSyncing ? 'Smart Syncing...' : '🔄 Smart Sync Orders'}
            </Button>
          </Tooltip>
          

          
          <Badge badgeContent={unpaidOrders?.data?.total || 0} color="error" max={999}>
            <PaymentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          </Badge>
        </Box>
      </Box>

      {/* Smart Sync Info */}
      <Card elevation={1} sx={{ mb: 3, backgroundColor: 'success.50', border: 1, borderColor: 'success.200' }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                🧠 Smart Sync Enabled
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Automatically detects and skips WooCommerce parent orders (different IDs like 55421 → 55422, 55423). 
                Only vendor-specific sub-orders are synced for clean accounting. No manual cleanup needed!
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {paymentSummary?.data && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Pending
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {formatCurrency(paymentSummary.data.overall.pending_amount)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {paymentSummary.data.overall.pending_payments} orders
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Paid
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(paymentSummary.data.overall.paid_amount)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {paymentSummary.data.overall.paid_payments} orders
                    </Typography>
                  </Box>
                  <PaidIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Platform Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                      {formatCurrency(paymentSummary.data.overall.total_platform_amount)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Commission earned
                    </Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {paymentSummary.data.overall.total_payments}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      All processed orders
                    </Typography>
                  </Box>
                  <OrdersIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper elevation={1}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
          <Tab 
            label={
              <Badge badgeContent={unpaidOrders?.data?.total || 0} color="error">
                Unpaid Orders
              </Badge>
            } 
            icon={<ReceiptIcon />} 
          />
          <Tab 
            label="Payment Batches" 
            icon={<PaymentIcon />} 
          />
          <Tab 
            label={
              <Badge badgeContent={vendorPaymentSummary?.data?.totals?.total_vendors || 0} color="warning">
                Vendor Payments
              </Badge>
            } 
            icon={<VendorsIcon />} 
          />
        </Tabs>

        {/* Unpaid Orders Tab */}
        <TabPanel value={currentTab} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Completed Orders Awaiting Payment ({unpaidOrders?.data?.total || 0})
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                size="small"
                onClick={handleSelectAllOrders}
              >
                {selectedOrders.length === (unpaidOrders?.data?.data?.length || 0) ? 'Deselect All' : 'Select All'}
              </Button>

              <Button
                variant="contained"
                startIcon={<CreateBatchIcon />}
                disabled={selectedOrders.length === 0}
                onClick={handleCreateBatch}
              >
                Create Payment Batch ({selectedOrders.length})
              </Button>
            </Stack>
          </Box>

          {unpaidError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load unpaid orders: {(unpaidError as Error).message}
            </Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'grey.50' }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={unpaidOrders?.data?.data && selectedOrders.length === unpaidOrders.data.data.length}
                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < (unpaidOrders?.data?.data?.length || 0)}
                        onChange={handleSelectAllOrders}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Order</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Items Subtotal</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Vendor Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Platform Fee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unpaidOrders?.data?.data?.map((order: UnpaidOrder) => (
                    <TableRow 
                      key={order.order_id} 
                      hover
                      selected={selectedOrders.includes(order.order_id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOrders.includes(order.order_id)}
                          onChange={() => handleSelectOrder(order.order_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          #{order.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(order.order_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.customer_name || 'Guest Customer'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {getVendorDisplayName(order.vendor_id, order.vendor_name)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {order.vendor_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(order.original_order_total || (order.order_total + (order.shipping_total || 0)))}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Items + Shipping
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(order.order_total)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Items only ({order.shipping_total ? formatCurrency(order.shipping_total) + ' shipping' : 'No shipping'})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {formatCurrency(order.vendor_amount)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          After {order.platform_commission_rate}% fee
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="info.main">
                          {formatCurrency(order.platform_amount)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {order.platform_commission_rate}% of items
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Payment Batches Tab */}
        <TabPanel value={currentTab} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Payment Batches
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              size="small"
            >
              Export Report
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <Table>
              <TableHead sx={{ backgroundColor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Batch Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Payment Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Amount</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Orders</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentBatches?.data?.map((batch: PaymentBatch) => (
                  <TableRow key={batch.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {batch.batch_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {batch.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(batch.payment_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={batch.status} 
                        color={getStatusColor(batch.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(batch.total_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {batch.total_orders}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ minWidth: 80 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {batch.paid_count}/{batch.payment_count}
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(batch.paid_count / batch.payment_count) * 100}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {batch.created_by_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(batch.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <Button 
                            size="small" 
                            startIcon={<ViewIcon />} 
                            variant="outlined"
                            onClick={() => handleViewBatch(batch)}
                          >
                            View
                          </Button>
                        </Tooltip>
                        <Tooltip title="Delete Batch">
                          <Button 
                            size="small" 
                            startIcon={<DeleteIcon />} 
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteBatch(batch.id, batch.batch_name)}
                            disabled={deleteBatchMutation.isPending}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Vendor Payments Tab */}
        <TabPanel value={currentTab} index={2}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Vendor Payments ({vendorPaymentSummary?.data?.totals?.total_vendors || 0} vendors)
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Total Due: {formatCurrency(vendorPaymentSummary?.data?.totals?.total_amount_due || 0)}
              </Typography>
              {Object.keys(vendorLookup).length > 0 && (
                <Chip 
                  label={`✅ ${Object.keys(vendorLookup).length} vendor names resolved`}
                  size="small" 
                  color="success" 
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          {vendorPaymentSummary?.data?.vendors?.map((vendor: VendorPaymentSummary) => (
            <Accordion key={vendor.vendor_id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {getVendorDisplayName(vendor.vendor_id, vendor.vendor_name)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {vendor.pending_orders} orders • ID: {vendor.vendor_id}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(vendor.total_amount_due)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Amount to pay
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Bank Details */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Payment Details
                        </Typography>
                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Email</Typography>
                            <Typography variant="body2">{getVendorEmail(vendor.vendor_id, vendor.vendor_email)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary">Payment Method</Typography>
                            <Typography variant="body2">{vendor.payment_method || 'Bank Transfer'}</Typography>
                          </Box>
                          {vendor.bank_details && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">Bank Details</Typography>
                              <Typography variant="body2">
                                {JSON.stringify(vendor.bank_details)}
                              </Typography>
                            </Box>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Button
                            variant="contained"
                            startIcon={<CreateBatchIcon />}
                            onClick={() => handleCreateVendorBatch(vendor)}
                            disabled={createBatchMutation.isPending}
                            fullWidth
                          >
                            Create Payment Batch
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Order Details */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Orders ({vendor.pending_orders})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Order ID</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vendor.orders.map((order) => (
                            <TableRow key={order.order_id}>
                              <TableCell>#{order.order_id}</TableCell>
                              <TableCell>{new Date(order.order_date).toLocaleDateString()}</TableCell>
                              <TableCell align="right">{formatCurrency(order.vendor_amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </TabPanel>
      </Paper>

      {/* Create Batch Dialog */}
      <Dialog open={createBatchDialogOpen} onClose={() => setCreateBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Payment Batch</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Batch Name"
              fullWidth
              value={batchFormData.batch_name}
              onChange={(e) => setBatchFormData(prev => ({ ...prev, batch_name: e.target.value }))}
            />
            <TextField
              label="Payment Date"
              type="date"
              fullWidth
              value={batchFormData.payment_date}
              onChange={(e) => setBatchFormData(prev => ({ ...prev, payment_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={batchFormData.notes}
              onChange={(e) => setBatchFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
            <Alert severity="info">
              You are creating a payment batch for {selectedOrders.length} orders.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBatchDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitBatch}
            disabled={createBatchMutation.isPending || !batchFormData.batch_name}
          >
            {createBatchMutation.isPending ? 'Creating...' : 'Create Batch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Detail Dialog */}
      <Dialog 
        open={batchDetailDialogOpen} 
        onClose={() => setBatchDetailDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{selectedBatch?.batch_name}</Typography>
              <Typography variant="body2" color="textSecondary">
                Payment Date: {selectedBatch && new Date(selectedBatch.payment_date).toLocaleDateString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label={selectedBatch?.status} 
                color={getStatusColor(selectedBatch?.status || '') as any}
              />
              {selectedBatch?.status === 'pending' && batchDetails && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleCompleteBatch(selectedBatch.id)}
                  disabled={completeBatchMutation.isPending}
                >
                  Mark Batch Complete
                </Button>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {batchDetails ? (
            <Box>
              {/* Batch Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(batchDetails.total_amount)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Amount
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" color="info.main">
                        {batchDetails.payments.length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Payments
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" color="success.main">
                        {batchDetails.payments.filter(p => p.payment_status === 'paid').length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Paid
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" color="warning.main">
                        {batchDetails.payments.filter(p => p.payment_status === 'pending').length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Pending
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Progress</Typography>
                  <Typography variant="body2">
                    {batchDetails.payments.filter(p => p.payment_status === 'paid').length} / {batchDetails.payments.length}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(batchDetails.payments.filter(p => p.payment_status === 'paid').length / batchDetails.payments.length) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Payments Table */}
              <Typography variant="h6" gutterBottom>
                Payments in this Batch
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment Ref</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchDetails.payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            #{payment.order_id}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(payment.order_date).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getVendorDisplayName(payment.vendor_id, payment.vendor_name)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {payment.vendor_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {payment.customer_name || 'Guest Customer'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(payment.vendor_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={payment.payment_status} 
                            color={getStatusColor(payment.payment_status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {payment.payment_reference || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {payment.payment_status === 'pending' && (
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Mark as Paid">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleMarkPaymentPaid(payment.id)}
                                  disabled={updatePaymentStatusMutation.isPending}
                                >
                                  Paid
                                </Button>
                              </Tooltip>
                              <Tooltip title="Mark as Failed">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleMarkPaymentFailed(payment.id)}
                                  disabled={updatePaymentStatusMutation.isPending}
                                >
                                  Failed
                                </Button>
                              </Tooltip>
                            </Stack>
                          )}
                          {payment.payment_status === 'paid' && (
                            <Chip label="Completed" color="success" size="small" />
                          )}
                          {payment.payment_status === 'failed' && (
                            <Chip label="Failed" color="error" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Batch Notes */}
              {batchDetails.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2">{batchDetails.notes}</Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Accounting; 